import moment from "moment-timezone";
import ISupplier_Delivery_Schedule from "../../../../models/ISupplier_Delivery_Schedule";
import ISupplier_Delivery_Schedule_Day from "../../../../models/ISupplier_Delivery_Schedule_Day";
import ISupplier_Delivery_Schedule_Override from "../../../../models/ISupplier_Delivery_Schedule_Override";
import IUpsertDeliveryScheduleDayDTO from "../../../../models/DTO/IUpsertDeliveryScheduleDayDTO";
import IUpsertDeliveryScheduleOverrideDTO from "../../../../models/DTO/IUpsertDeliveryScheduleOverrideDTO";
import { MAX_CUSTOMER_DELIVERY_DAY_OPTIONS } from "../../../../models/DeliveryDeadlineConstants";

/**
 * Day-of-week is 0=Sunday .. 6=Saturday everywhere in this feature.
 * DAY_LABEL_KEYS are i18n keys (full names) and DAY_ABBR_KEYS are short forms,
 * both translated through OwLabel / FormattedMessage at render time.
 */
export const DAY_LABEL_KEYS = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];
export const DAY_ABBR_KEYS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];

/**
 * Timezone used when neither the vendor nor the supplier has one. Must match the API's
 * DEFAULT_DELIVERY_TZ (OrderService) so the preview never computes a different cutoff than the
 * server stamps on submit.
 */
export const DEFAULT_DELIVERY_TIMEZONE = "America/New_York";

/** Options for a day-of-week OwSelectOption (value is the 0-6 index). */
export const DAY_OPTIONS = DAY_LABEL_KEYS.map((key, index) => ({ value: index, labelKey: key }));

/**
 * Normalizes a time value for sending to the API: ensures "HH:mm" (drops seconds).
 * Returns null for empty input so all-or-nothing soft-reminder handling stays clean.
 */
export const toApiTime = (value?: string | null): string | null => {
    if (!value) return null;
    const parts = value.split(":");
    if (parts.length < 2) return null;
    const [h, m] = parts;
    return `${h.padStart(2, "0")}:${m.padStart(2, "0")}`;
};

/**
 * Normalizes a time value from the API (tolerates "HH:mm:ss") into the "HH:mm"
 * form that a native <input type="time"> expects.
 */
export const fromApiTime = (value?: string | null): string => {
    if (!value) return "";
    const parts = value.split(":");
    if (parts.length < 2) return "";
    const [h, m] = parts;
    return `${h.padStart(2, "0")}:${m.padStart(2, "0")}`;
};

/** Formats an "HH:mm"/"HH:mm:ss" time as 12-hour (e.g. "6:00 PM"). */
export const format12h = (time?: string | null): string => {
    if (!time) return "";
    const [hours, minutes] = time.split(":");
    const h = parseInt(hours, 10);
    if (isNaN(h)) return "";
    const ampm = h >= 12 ? "PM" : "AM";
    const h12 = h % 12 || 12;
    return `${h12}:${(minutes ?? "00").padStart(2, "0")} ${ampm}`;
};

/** Returns true when the time's minutes are exactly :00. */
export const isOnTheHour = (value?: string | null): boolean => {
    if (!value) return false;
    const parts = value.split(":");
    return parts.length >= 2 && parts[1] === "00";
};

/**
 * Sorts a schedule's days by delivery_day_of_week (Sun-first) for stable display.
 */
export const sortDays = (days?: ISupplier_Delivery_Schedule_Day[]): ISupplier_Delivery_Schedule_Day[] => {
    return [...(days || [])].sort((a, b) => a.delivery_day_of_week - b.delivery_day_of_week);
};

/**
 * Editable form state for a single delivery day. Times are kept in "HH:mm" form
 * for native time inputs; soft reminder is optional and all-or-nothing.
 */
export interface IScheduleDayFormState {
    delivery_day_of_week: number;
    hard_deadline_day_of_week: number;
    hard_deadline_time: string; // "HH:mm"
    send_reminder: boolean;
    soft_reminder_day_of_week: number;
    soft_reminder_time: string; // "HH:mm"
    minimum_order_total: string; // raw input; "" = no minimum (unchanged binary behavior)
}

/** Builds default form state for a brand-new day. */
export const defaultDayFormState = (): IScheduleDayFormState => ({
    delivery_day_of_week: 2, // Tuesday – a sensible non-weekend default
    hard_deadline_day_of_week: 1, // Monday
    hard_deadline_time: "18:00",
    send_reminder: false,
    soft_reminder_day_of_week: 1,
    soft_reminder_time: "12:00",
    minimum_order_total: "",
});

/** Maps an existing API day onto editable form state. */
export const dayToFormState = (day: ISupplier_Delivery_Schedule_Day): IScheduleDayFormState => ({
    delivery_day_of_week: day.delivery_day_of_week,
    hard_deadline_day_of_week: day.hard_deadline_day_of_week,
    hard_deadline_time: fromApiTime(day.hard_deadline_time),
    send_reminder: day.soft_reminder_day_of_week != null && day.soft_reminder_time != null,
    soft_reminder_day_of_week: day.soft_reminder_day_of_week ?? 1,
    soft_reminder_time: fromApiTime(day.soft_reminder_time),
    minimum_order_total: day.minimum_order_total != null ? String(day.minimum_order_total) : "",
});

/**
 * Validation errors for a day form keyed by field. An empty object means valid.
 * Values are i18n keys so the caller can render them via OwLabel/FormattedMessage.
 */
export type ScheduleDayErrors = Partial<
    Record<"hard_deadline_time" | "soft_reminder_time" | "soft_reminder" | "minimum_order_total", string>
>;

/**
 * Enforces the domain rules client-side:
 * - hard deadline time required
 * - soft reminder all-or-nothing (handled here via the send_reminder toggle)
 * - soft reminder minutes must be :00
 */
export const validateScheduleDay = (state: IScheduleDayFormState): ScheduleDayErrors => {
    const errors: ScheduleDayErrors = {};

    if (!state.hard_deadline_time) {
        errors.hard_deadline_time = "field_required";
    }

    if (state.send_reminder) {
        if (!state.soft_reminder_time) {
            errors.soft_reminder = "soft_reminder_all_or_nothing";
        } else if (!isOnTheHour(state.soft_reminder_time)) {
            errors.soft_reminder_time = "reminder_minutes_must_be_zero";
        }
    }

    if (state.minimum_order_total.trim() !== "") {
        const amount = Number(state.minimum_order_total);
        if (!isFinite(amount) || amount < 0) {
            errors.minimum_order_total = "invalid_amount";
        }
    }

    return errors;
};

/**
 * Converts validated form state into the upsert DTO. Soft reminder fields are
 * sent together or both nulled, satisfying the all-or-nothing rule.
 */
export const formStateToDTO = (state: IScheduleDayFormState): IUpsertDeliveryScheduleDayDTO => ({
    delivery_day_of_week: state.delivery_day_of_week,
    hard_deadline_day_of_week: state.hard_deadline_day_of_week,
    hard_deadline_time: toApiTime(state.hard_deadline_time) || "00:00",
    soft_reminder_day_of_week: state.send_reminder ? state.soft_reminder_day_of_week : null,
    soft_reminder_time: state.send_reminder ? toApiTime(state.soft_reminder_time) : null,
    minimum_order_total: state.minimum_order_total.trim() === "" ? null : Number(state.minimum_order_total),
});

/** Human-readable summary of a schedule's delivery days, e.g. "Tue, Thu". */
export const deliveryDaysSummary = (schedule: ISupplier_Delivery_Schedule, abbr: (key: string) => string): string => {
    const days = sortDays(schedule.Days);
    if (!days.length) return "";
    return days.map((d) => abbr(DAY_ABBR_KEYS[d.delivery_day_of_week])).join(", ");
};

/**
 * Whether the delivery-deadlines feature is enabled for a vendor, i.e. it has a
 * delivery schedule assigned. Tolerant of either the scalar id or the populated
 * association being present.
 */
export const isDeliveryDeadlinesEnabled = (
    vendor?: { delivery_schedule_id?: number | null; Delivery_Schedule?: ISupplier_Delivery_Schedule | null } | null
): boolean => !!(vendor?.delivery_schedule_id || vendor?.Delivery_Schedule);

/** A resolved upcoming delivery: the delivery date, its hard deadline, and where it came from. */
export interface INextDeliveryResult {
    /** The template day this came from; absent for a schedule-exception (override) date. */
    day?: ISupplier_Delivery_Schedule_Day;
    deliveryDate: moment.Moment; // start of the delivery day, in the vendor timezone
    hardDeadline: moment.Moment; // deadline datetime, in the vendor timezone
    hardDeadlineTime: string; // "HH:mm" wall-clock time of the deadline, for display
    isOverride: boolean; // true when the date comes from a holiday-week schedule exception
}

/** Sunday (YYYY-MM-DD) that starts the week containing the given calendar date. */
export const weekStartOf = (date: moment.Moment): string => date.clone().subtract(date.day(), "days").format("YYYY-MM-DD");

/** Normalizes a delivery date (YYYY-MM-DD, or an ISO UTC-midnight timestamp from Orders.delivery_date) to YYYY-MM-DD. */
export const toDeliveryDateKey = (value?: string | Date | null): string | null => {
    if (!value) return null;
    if (value instanceof Date) return moment.utc(value).format("YYYY-MM-DD");
    return String(value).slice(0, 10);
};

/**
 * How many upcoming delivery dates a schedule offers at checkout: its configured count when customer
 * choice is enabled, otherwise just the soonest. Clamped to 1..MAX_CUSTOMER_DELIVERY_DAY_OPTIONS.
 */
export const getDeliveryOptionCount = (schedule?: ISupplier_Delivery_Schedule | null): number => {
    if (!schedule?.customer_can_choose_delivery_day) return 1;
    const count = Math.trunc(Number(schedule.customer_delivery_day_options) || 1);
    return Math.min(Math.max(count, 1), MAX_CUSTOMER_DELIVERY_DAY_OPTIONS);
};

/**
 * Computes the next `count` achievable deliveries across all of a schedule's days, earliest first.
 *
 * "Achievable" means the day's hard deadline has not yet passed and the delivery date is not a
 * supplier holiday (blackoutDates, YYYY-MM-DD). Mirrors the API's upcomingDeliveryDates so the
 * preview never shows a date the server would refuse. All math is done in the vendor timezone;
 * day-of-week is 0=Sun..6=Sat, matching the rest of this feature.
 *
 * Returns [] when there is no schedule / no days, or when no upcoming cycle within the search
 * window still has an open deadline (caller can treat that as "passed").
 */
export const getUpcomingDeliveries = (
    schedule: ISupplier_Delivery_Schedule | null | undefined,
    timezone?: string | null,
    count = 1,
    blackoutDates?: Iterable<string> | null,
    now?: moment.Moment,
    overrides?: ISupplier_Delivery_Schedule_Override[] | null
): INextDeliveryResult[] => {
    const days = sortDays(schedule?.Days);

    // Holiday-week exceptions keyed by their Sunday. In such a week the template is ignored: a
    // no-delivery exception yields nothing, otherwise its explicit dates are used (not holiday-filtered).
    const overrideByWeek = new Map<string, ISupplier_Delivery_Schedule_Override>();
    for (const override of overrides || []) {
        if (!override?.week_start) continue;
        const key = String(override.week_start).slice(0, 10);
        const existing = overrideByWeek.get(key);
        // A template-specific exception beats a supplier-wide (schedule_id null) one for the same week,
        // whatever order the rows arrive in. Mirrors the API.
        if (!existing || (existing.schedule_id == null && override.schedule_id != null)) overrideByWeek.set(key, override);
    }

    if (!days.length && !overrideByWeek.size) return [];

    const wanted = Math.min(Math.max(Math.trunc(Number(count) || 1), 1), MAX_CUSTOMER_DELIVERY_DAY_OPTIONS);
    const blackout = new Set(blackoutDates || []);
    const tz = timezone || DEFAULT_DELIVERY_TIMEZONE;
    const ref = (now ? now.clone() : moment.tz(tz)).tz(tz);
    const refDay = ref.clone().startOf("day");

    const results: INextDeliveryResult[] = [];
    const horizonWeeks = 7 + wanted;

    // Scan up to 8 weeks past the requested count so passed deadlines and multi-day holiday
    // ranges (which can knock out several consecutive deliveries) still fill the list.
    for (let offset = 0; offset <= horizonWeeks && results.length < wanted; offset++) {
        for (const day of days) {
            // Next occurrence of the delivery day at/after today, plus `offset` weeks.
            const forward = (day.delivery_day_of_week - refDay.day() + 7) % 7;
            const deliveryDate = refDay.clone().add(forward + offset * 7, "days");

            if (overrideByWeek.has(weekStartOf(deliveryDate))) continue;
            if (blackout.has(deliveryDate.format("YYYY-MM-DD"))) continue;

            // Hard deadline: the occurrence of hard_deadline_day_of_week at/before the
            // delivery date (within the preceding 7 days), at hard_deadline_time.
            const back = (deliveryDate.day() - day.hard_deadline_day_of_week + 7) % 7;
            const hardDeadline = deliveryDate.clone().subtract(back, "days");
            const [hh, mm] = fromApiTime(day.hard_deadline_time).split(":");
            hardDeadline
                .hour(parseInt(hh, 10) || 0)
                .minute(parseInt(mm, 10) || 0)
                .second(0)
                .millisecond(0);

            // Skip any cycle whose deadline has already passed.
            if (hardDeadline.isBefore(ref)) continue;

            results.push({ day, deliveryDate, hardDeadline, hardDeadlineTime: fromApiTime(day.hard_deadline_time), isOverride: false });
        }
    }

    // Exception dates: explicit staff choices within the horizon whose order-by deadline is still open.
    const horizonEnd = refDay.clone().add((horizonWeeks + 1) * 7, "days");
    for (const override of overrideByWeek.values()) {
        if (override.no_delivery) continue;
        for (const day of override.Days || []) {
            const deliveryDate = moment.tz(String(day.delivery_date).slice(0, 10), "YYYY-MM-DD", tz);
            if (!deliveryDate.isValid() || deliveryDate.isBefore(refDay) || deliveryDate.isAfter(horizonEnd)) continue;

            const [hh, mm] = fromApiTime(day.hard_deadline_time).split(":");
            const hardDeadline = moment
                .tz(String(day.hard_deadline_date).slice(0, 10), "YYYY-MM-DD", tz)
                .hour(parseInt(hh, 10) || 0)
                .minute(parseInt(mm, 10) || 0)
                .second(0)
                .millisecond(0);
            if (!hardDeadline.isValid() || hardDeadline.isBefore(ref)) continue;

            results.push({ deliveryDate, hardDeadline, hardDeadlineTime: fromApiTime(day.hard_deadline_time), isOverride: true });
        }
    }

    // Every template week's candidates fall strictly after the previous week's and exception dates
    // are unique per week, so sorting by delivery date orders the whole list.
    results.sort((a, b) => a.deliveryDate.valueOf() - b.deliveryDate.valueOf() || a.hardDeadline.valueOf() - b.hardDeadline.valueOf());

    return results.slice(0, wanted);
};

/**
 * Computes the soonest achievable delivery across all of a schedule's days: the first entry of
 * getUpcomingDeliveries, so the single-date preview and the multi-date picker always agree.
 *
 * Returns null when there is no schedule / no days, or when no upcoming cycle within the search
 * window still has an open deadline (caller can treat that as "passed").
 */
export const getNextDelivery = (
    schedule: ISupplier_Delivery_Schedule | null | undefined,
    timezone?: string | null,
    now?: moment.Moment,
    blackoutDates?: Iterable<string> | null,
    overrides?: ISupplier_Delivery_Schedule_Override[] | null
): INextDeliveryResult | null => {
    const [first] = getUpcomingDeliveries(schedule, timezone, 1, blackoutDates, now, overrides);
    return first || null;
};

/** Editable form state for a schedule exception (one calendar week). Dates are "YYYY-MM-DD", times "HH:mm". */
export interface IOverrideDayFormState {
    delivery_date: string;
    hard_deadline_date: string;
    hard_deadline_time: string;
}

export interface IOverrideFormState {
    schedule_guid: string; // "" = all templates
    week_start: string; // any date in the week; normalized to its Sunday on save
    no_delivery: boolean;
    note: string;
    days: IOverrideDayFormState[];
}

/** Default exception form: next week, replacement days mode, one blank row. */
export const defaultOverrideFormState = (now: moment.Moment = moment()): IOverrideFormState => {
    const nextWeek = now.clone().startOf("day").add(7, "days");
    const sunday = nextWeek.clone().subtract(nextWeek.day(), "days");
    return {
        schedule_guid: "",
        week_start: sunday.format("YYYY-MM-DD"),
        no_delivery: false,
        note: "",
        days: [defaultOverrideDayFormState(sunday)],
    };
};

/** A blank exception day: Wednesday of the week, order by Monday 18:00. */
export const defaultOverrideDayFormState = (weekStart: moment.Moment): IOverrideDayFormState => ({
    delivery_date: weekStart.clone().add(3, "days").format("YYYY-MM-DD"),
    hard_deadline_date: weekStart.clone().add(1, "days").format("YYYY-MM-DD"),
    hard_deadline_time: "18:00",
});

/** Maps an existing exception onto editable form state. */
export const overrideToFormState = (override: ISupplier_Delivery_Schedule_Override): IOverrideFormState => ({
    schedule_guid: override.Delivery_Schedule?.guid || "",
    week_start: String(override.week_start).slice(0, 10),
    no_delivery: !!override.no_delivery,
    note: override.note || "",
    days: (override.Days || []).map((day) => ({
        delivery_date: String(day.delivery_date).slice(0, 10),
        hard_deadline_date: String(day.hard_deadline_date).slice(0, 10),
        hard_deadline_time: fromApiTime(day.hard_deadline_time),
    })),
});

/** Validation errors for an exception form; values are i18n keys. Day errors are keyed by row index. */
export interface OverrideFormErrors {
    week_start?: string;
    days?: string;
    rows?: Record<number, string>;
}

/**
 * Client-side mirror of the API rules: a valid week, every delivery date inside that week, the order-by
 * date on/before (and within two weeks of) its delivery date with a time, no duplicate dates, and at
 * least one day unless the week is marked no delivery.
 */
export const validateOverrideForm = (state: IOverrideFormState): OverrideFormErrors => {
    const errors: OverrideFormErrors = {};
    const week = moment(state.week_start, "YYYY-MM-DD", true);

    if (!week.isValid()) {
        errors.week_start = "field_required";
        return errors;
    }

    if (state.no_delivery) return errors;

    if (!state.days.length) {
        errors.days = "exception_days_required";
        return errors;
    }

    const weekStart = week.clone().subtract(week.day(), "days");
    const weekEnd = weekStart.clone().add(6, "days");
    const rows: Record<number, string> = {};
    const seen = new Set<string>();

    state.days.forEach((day, index) => {
        const delivery = moment(day.delivery_date, "YYYY-MM-DD", true);
        const deadline = moment(day.hard_deadline_date, "YYYY-MM-DD", true);

        if (!delivery.isValid() || !deadline.isValid() || !day.hard_deadline_time) {
            rows[index] = "field_required";
        } else if (delivery.isBefore(weekStart) || delivery.isAfter(weekEnd)) {
            rows[index] = "exception_date_outside_week";
        } else if (deadline.isAfter(delivery) || delivery.diff(deadline, "days") > 14) {
            rows[index] = "exception_deadline_after_delivery";
        } else if (seen.has(day.delivery_date)) {
            rows[index] = "exception_duplicate_date";
        }

        seen.add(day.delivery_date);
    });

    if (Object.keys(rows).length) errors.rows = rows;

    return errors;
};

/** Converts validated form state into the upsert DTO (days omitted for a no-delivery week). */
export const overrideFormStateToDTO = (state: IOverrideFormState): IUpsertDeliveryScheduleOverrideDTO => ({
    schedule_guid: state.schedule_guid || null,
    week_start: state.week_start,
    no_delivery: state.no_delivery,
    note: state.note.trim() || null,
    days: state.no_delivery
        ? []
        : state.days.map((day) => ({
              delivery_date: day.delivery_date,
              hard_deadline_date: day.hard_deadline_date,
              hard_deadline_time: toApiTime(day.hard_deadline_time) || "00:00",
          })),
});
